import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import RecruitmentHeader from '../../assets/RecruitmentHeader.svg';
import RecruitmentHeaderMobileView from '../../assets/RecruitmentHeaderMobileView.svg';
import {
	GetRecruitmentPagesFn,
	GetAshlinkPublicUserRedirectionsFn,
} from '../../data/apiCalls';
import { LOGIN_URL } from '../../scripts/navigationItems';
import {
	contentSpecialtyMap,
	mapRegionSpecialtyString,
	validRegionSpecialtyString,
} from '../../scripts/recruitmentPageHelpers';
import RecruitmentPageTab from './RecruitmentPageTab';

const RecruitmentPage = () => {
	const theme = useTheme();
	const isExtraSmall = useMediaQuery(theme.breakpoints.down('sm'));
	const { regionSpecialtyString } = useParams();
	const { specialty, region } = mapRegionSpecialtyString(
		regionSpecialtyString
	);
	// If region specialty string is not valid redirect to login page
	if (!validRegionSpecialtyString(regionSpecialtyString)) {
		window.open(LOGIN_URL, '_self', 'noreferrer');
	}
	const {
		data: {
			data: { data: { ashlinkUserRedirections = [] } = {} } = {},
		} = {},
	} = useQuery(
		['userRedirections'],
		() => GetAshlinkPublicUserRedirectionsFn(),
		{
			staleTime: Infinity,
		}
	);
	// if feature flag is off, redirect to legacy recruitment page
	useEffect(() => {
		if (ashlinkUserRedirections?.length > 0) {
			const recruitmentRedirects = ashlinkUserRedirections.filter(
				redirect => redirect.pageKey === 'recruitment'
			);
			const redirectURL = recruitmentRedirects[0]?.redirectUrl;
			if (redirectURL) {
				window.open(redirectURL + regionSpecialtyString, '_self');
			}
		}
	}, [ashlinkUserRedirections]);

	const [selectedTab, setSelectedTab] = useState(0);
	const handleChange = (event, newTab) => {
		setSelectedTab(newTab);
	};

	const {
		data: {
			status = undefined,
			data: { resultCode = undefined, data: { pages = [] } = {} } = {},
		} = {},
		isLoading,
		isError,
	} = useQuery(
		['RecruitmentPages'],
		() => GetRecruitmentPagesFn(specialty, region),
		{
			staleTime: Infinity,
		}
	);
	const [mappedPages, setMappedPages] = useState([]);
	useEffect(() => {
		if (pages?.length > 0) {
			const tempMappedPages = {};
			pages.forEach(page => {
				tempMappedPages[page.pageType] = page;
			});

			setMappedPages(tempMappedPages);
		}
	}, [isLoading]);
	const userType =
		specialty === contentSpecialtyMap.rt ? 'Provider' : 'Practitioner';

	const tabs = [
		{
			label: 'Introduction',
			pageTitle: 'ASH Introduction',
			id: 'intro',
			value: 0,
			pageTypeForDisplay: 'Introduction',
		},
		{
			label: `${userType} Services Agreement`,
			id: 'practitioner-services-agreements',
			value: 1,
			pageTypeForDisplay: 'Practitioner/Provider Services Agreement',
		},
		{
			label: `${userType} Resources`,
			id: 'practitioner-resources',
			value: 2,
			pageTypeForDisplay: 'Practitioner/Provider Resources',
		},
		{
			label: `${userType} Highlights`,
			id: 'participation-highlights',
			value: 3,
			pageTypeForDisplay: 'Practitioner/Provider Highlights',
		},
		{
			label: 'Ready to Join!',
			id: 'ready-to-join',
			value: 4,
			pageTypeForDisplay: 'Ready to Join',
		},
	];

	return (
		<Container
			disableGutters={useMediaQuery(
				theme.breakpoints.between('xs', 'md')
			)}
		>
			<Box
				mt={isExtraSmall ? 1 : 3}
				mb={isExtraSmall ? 2 : 5}
				mx={isExtraSmall && 2}
				sx={{
					backgroundImage: `url(${
						isExtraSmall
							? RecruitmentHeaderMobileView
							: RecruitmentHeader
					})`,
					height: isExtraSmall ? '130px' : '237px',
					backgroundSize: 'cover',
				}}
			/>

			<Box mb={isExtraSmall ? 2 : 5}>
				<Tabs
					value={selectedTab}
					onChange={handleChange}
					variant='scrollable'
					scrollButtons='auto'
					allowScrollButtonsMobile
					aria-label='scrollable tabs'
					sx={{ pb: 3 }}
				>
					{tabs.map(tab => (
						<Tab
							label={tab.label}
							id={tab.id}
							key={tab.id}
							sx={{ textTransform: 'capitalize' }}
						/>
					))}
				</Tabs>
				{tabs.map((tab, index) => (
					<RecruitmentPageTab
						key={tab.id}
						tab={tab}
						page={mappedPages[tab.pageTypeForDisplay]}
						resultCode={resultCode}
						statusCode={status}
						isError={isError}
						isSelected={selectedTab === index}
						isLoading={isLoading}
					/>
				))}
			</Box>
		</Container>
	);
};

export default RecruitmentPage;
